import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Typography } from '@mui/material';
import { CheckCircle, Edit, Event, Tag } from '@mui/icons-material';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDateOnly } from '../../Formatters';
import { GuidePriceModel } from '../../interfaces';
import { BadgePill, ContactChip } from '../../users/details/Hero';
import { Card } from '../../users/details/Card';
import {
  CANCEL_PRICE_UPDATE as CANCEL_GUIDE_PRICE_UPDATE,
  CHANGE_SALES_TEXT,
  GET_PRICE,
  UPDATE_PRICE as UPDATE_GUIDE_PRICE,
} from '../../pdf/pdf.graphql';
import {
  CANCEL_HISTORICAL_PRICE_UPDATE,
  CHANGE_HISTORICAL_SALES_TEXT,
  GET_HISTORICAL_PRICE,
  UPDATE_HISTORICAL_PRICE,
} from '../../historical/historical.graphql';
import { SalesPointsList, SalesTextField, salesTextError } from '../salesText';
import { PriceChangeDialog } from '../PriceChangeDialog';
import { GuideVersion, guideVersions } from '../status';
import { ProductHero, detailsGridSx, detailsPageSx } from './ProductHero';
import { PriceCard } from './PriceCard';
import { ChangesPanel } from './ChangesPanel';

export default function GuideDetails() {
  const { version } = useParams<{ version: string }>();
  if (version !== 'gjeldende' && version !== 'historisk')
    return (
      <Box sx={detailsPageSx}>
        <Typography variant="h5">Fant ikke produktet</Typography>
        <Link component={RouterLink} to="/product">
          Til produktene
        </Link>
      </Box>
    );
  // Keyed, so going from one version to the other starts with fresh queries and state.
  return <GuidePrice key={version} version={version} />;
}

function GuidePrice({ version }: { version: GuideVersion }) {
  const historical = version === 'historisk';
  const { kind, title, number } = guideVersions[version];
  const [changingPrice, setChangingPrice] = useState(false);
  const [editingSalesText, setEditingSalesText] = useState(false);

  const { data, loading, error } = useQuery<{ pdf?: GuidePriceModel; historical?: GuidePriceModel }>(historical ? GET_HISTORICAL_PRICE : GET_PRICE);
  const [cancelPriceChange, { loading: cancelling }] = useMutation(historical ? CANCEL_HISTORICAL_PRICE_UPDATE : CANCEL_GUIDE_PRICE_UPDATE, {
    refetchQueries: ['GetProductChanges'],
  });
  const [changePrice] = useMutation(historical ? UPDATE_HISTORICAL_PRICE : UPDATE_GUIDE_PRICE, {
    refetchQueries: [{ query: historical ? GET_HISTORICAL_PRICE : GET_PRICE }, 'GetProductChanges'],
  });
  const [changeSalesText] = useMutation(historical ? CHANGE_HISTORICAL_SALES_TEXT : CHANGE_SALES_TEXT, {
    refetchQueries: [{ query: historical ? GET_HISTORICAL_PRICE : GET_PRICE }, 'GetProductChanges'],
  });

  if (error) return <Box sx={detailsPageSx}>Feil! {error.message}</Box>;
  const guide = historical ? data?.historical : data?.pdf;
  if (loading || !guide) return <LoadingSpinner />;
  const pending = guide.pendingPriceChange;

  return (
    <Box sx={detailsPageSx}>
      <ProductHero
        pills={
          pending ? (
            <BadgePill tone="warning" icon={<Event />}>
              Ny pris {formatDateOnly(pending.timeOfChange)}
            </BadgePill>
          ) : (
            <BadgePill tone="success" icon={<CheckCircle />}>
              Til salgs
            </BadgePill>
          )
        }
        title={title}
        chips={<ContactChip icon={<Tag fontSize="inherit" />}>Maconomy-nr. {number}</ContactChip>}
      />

      <Box sx={detailsGridSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2.5 } }}>
          <PriceCard
            prices={[{ label: 'Per anvisning', current: guide.price, next: pending?.newPrice }]}
            pendingFrom={pending?.timeOfChange}
            cancelling={cancelling}
            canChange
            onChange={() => setChangingPrice(true)}
            onCancel={() => cancelPriceChange()}
          />
          <Card title="Gjelder">
            <Typography variant="body2" sx={{ px: 1.5, py: 1 }}>
              {historical ? 'Versjoner som er erstattet av en nyere.' : 'Den gjeldende versjonen av hver anvisning.'}
            </Typography>
          </Card>
          <Card
            title="Salgstekst"
            action={
              <Button size="small" startIcon={<Edit />} onClick={() => setEditingSalesText(true)}>
                Rediger
              </Button>
            }
          >
            <Box sx={{ px: 1.5, py: 1, typography: 'body2' }}>
              {guide.salesText ? (
                <SalesPointsList text={guide.salesText} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Ingen
                </Typography>
              )}
            </Box>
          </Card>
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'fontWeightMedium', px: { xs: 2, sm: 3 }, py: { xs: 1.75, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}
          >
            Endringer
          </Typography>
          <ChangesPanel kind={kind} revision={[guide.price, pending?.id]} />
        </Paper>
      </Box>

      {changingPrice && (
        <PriceChangeDialog
          product={title}
          fields={[{ label: 'Per anvisning', current: guide.price }]}
          onSave={([price], timeOfChange) => changePrice({ variables: { input: { price, timeOfChange: timeOfChange.toISOString() } } })}
          onClose={() => setChangingPrice(false)}
        />
      )}
      {editingSalesText && (
        <SalesTextDialog
          title={title}
          historical={historical}
          salesText={guide.salesText ?? ''}
          onSave={(salesText) => changeSalesText({ variables: { input: { salesText: salesText.trim() || null } } })}
          onClose={() => setEditingSalesText(false)}
        />
      )}
    </Box>
  );
}

interface SalesTextDialogProps {
  title: string;
  historical: boolean;
  salesText: string;
  onSave(salesText: string): Promise<unknown>;
  onClose(): void;
}

function SalesTextDialog({ title, historical, salesText: initial, onSave, onClose }: SalesTextDialogProps) {
  const [salesText, setSalesText] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(salesText);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fikk ikke lagret salgsteksten.');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Salgstekst for {title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SalesTextField
            value={salesText}
            onChange={setSalesText}
            about={
              historical
                ? 'Listes på Enkeltanvisning-kortet i portalen når en versjon som er erstattet av en nyere kjøpes.'
                : 'Listes på Enkeltanvisning-kortet i portalen når den gjeldende versjonen kjøpes.'
            }
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Avbryt
        </Button>
        <Button variant="contained" disabled={salesTextError(salesText) !== null} loading={saving} onClick={save}>
          Lagre
        </Button>
      </DialogActions>
    </Dialog>
  );
}
